import { request, getApiUrl, generateApiUrl } from '@/utils/ajax';
import { ProfileCatalogProduct, ProductFile, CatalogFilterData, CatalogProductImportResult, CatalogProductImportPreviewResult } from '@/types';

export function saveProfileCatalogProduct(
    product: Partial<ProfileCatalogProduct>,
    files: ProductFile[]
): Promise<ProfileCatalogProduct> {
    return request(`api/profile-catalog-product`, {
        method: 'POST',
        json: true,
        body: {
            product,
            files,
        },
    }).then((x) => x.json());
}

export function deleteProfileCatalogProduct(catalogProductId: number) {
    return request(`api/profile-catalog-product/${catalogProductId}`, {
        method: 'DELETE',
    });
}

export function getProfileCatalogProductsByGroup(groupId: number): Promise<ProfileCatalogProduct[]> {
    return request(`api/profile-catalog-group/${groupId}/products`).then((x) => x.json());
}

export function getProfileCatalogProductsByOwner(profileId: number): Promise<ProfileCatalogProduct[]> {
    return searchProfileCatalogProducts({
        ownerProfileId: profileId,
    });
}

export function getProfileCatalogProduct(productId: number): Promise<ProfileCatalogProduct> {
    return request(`api/profile-catalog-product/${productId}`).then((x) => x.json());
}

interface ProfileCatalogProductsSearchOptions {
    searchText?: string | null;
    itemCategoryIds?: number[] | null;
    ownerProfileId: number;
    suppliers?: string[] | null;
}

export function searchProfileCatalogProducts(options: ProfileCatalogProductsSearchOptions): Promise<ProfileCatalogProduct[]> {
    return request(`api/profile-catalog-product`, {
        query: options,
    }).then((x) => x.json());
}

export function getProfileCatalogFilters(profileId: number): Promise<CatalogFilterData> {
    return request(`api/profile-catalog-product/filter-data`, {
        query: { profileId },
    }).then((x) => x.json());
}

export function getProfileCatalogProductFileUrl(productImageId: number, download: boolean = false) {
    return getApiUrl() + `/api/profile-catalog-product-image/${productImageId}/${download ? 'download' : 'open'}`;
}

export function getProfileCatalogProductFileThumbnailUrl(productImageId: number) {
    return getApiUrl() + `/api/profile-catalog-product-image/${productImageId}/thumbnail`;
}

export function getImportableCatalogProducts(profileId: number, projectId: number): Promise<ProjectImportableProduct[]> {
    return request(`api/profile-catalog-product/project-import`, { query: { profileId, projectId } }).then((x) => x.json());
}

export function importProductsToProject(projectId: number, profileCatalogProductIds: number[]): Promise<any> {
    return request(`api/profile-catalog-product/project-import`, {
        method: 'POST',
        json: true,
        body: {
            projectId,
            profileCatalogProductIds,
        },
    });
}

export function getConflictsWithProjectCatalog(
    projectId: number,
    profileProductIds: number[]
): Promise<ProfileCatalogProduct[]> {
    return request(`api/profile-catalog-product/conflicts`, {
        query: { projectId, profileProductIds },
    }).then((x) => x.json());
}

export function getCatalogProductExportUrl(profileId: number): string {
    return generateApiUrl('/api/profile-catalog-product/export', { profileId });
}

export function getCatalogProductImportTemplateUrl(): string {
    return generateApiUrl('/api/profile-catalog-product/import-template');
}

export function importCatalogProductsFromExcel(profileId: number, file: File): Promise<CatalogProductImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    return request(`api/profile-catalog-product/import`, {
        method: 'POST',
        body: formData,
        query: { profileId },
    }).then((x) => x.json());
}

export function previewCatalogProductImport(profileId: number, file: File): Promise<CatalogProductImportPreviewResult> {
    const formData = new FormData();
    formData.append('file', file);
    return request(`api/profile-catalog-product/import-preview`, {
        method: 'POST',
        body: formData,
        query: { profileId },
    }).then((x) => x.json());
}

export interface ProjectImportableProduct {
    product: ProfileCatalogProduct;
    isAlreadyImported: boolean;
    hasPermissionsToImport: boolean;
    isImportable: boolean;
}